import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "../../ui/Button";
import PageState from "../../ui/PageState";
import { getPolicySummary } from "../../api/policyDashboard.api";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";

const emptySummary = {
  pending_approval: { count: 0, items: [] },
  draft_policies: { count: 0, items: [] },
  overdue_reviews: { count: 0, items: [] },
  low_ack: { count: 0, items: [] },
  controls_without_published_policy: { count: 0, items: [] },
};

const widgets = [
  {
    key: "pending_approval",
    title: "Pending Approval",
    subtitle: "Submitted versions waiting for approval.",
    fallbackPath: "/catalog/policies",
  },
  {
    key: "draft_policies",
    title: "Drafts",
    subtitle: "Active tenant policies with draft versions.",
    fallbackPath: "/catalog/policies",
  },
  {
    key: "overdue_reviews",
    title: "Overdue Reviews",
    subtitle: "Policies where review date has passed.",
    fallbackPath: "/catalog/policies",
  },
  {
    key: "low_ack",
    title: "Low Acknowledgement",
    subtitle: "Published policies below 80% acknowledgement.",
    fallbackPath: "/catalog/policies",
  },
  {
    key: "controls_without_published_policy",
    title: "Controls Missing Published Policy",
    subtitle: "Controls not covered by an active published policy.",
    fallbackPath: "/controls",
  },
];

const sectionStyle = {
  background: colors.surface,
  borderRadius: radius.lg,
  border: `1px solid ${colors.border}`,
  padding: spacing.xl * 2,
};

const cardStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: spacing.xl,
  background: colors.surfaceSubtle,
  display: "grid",
  gap: spacing.lg,
  boxShadow: shadows.xs,
};

function toPolicyPath(item, fallbackPath) {
  if (item?.tenant_policy_id) {
    return `/catalog/policies/${item.tenant_policy_id}`;
  }
  return fallbackPath;
}

function toControlPath(item, fallbackPath) {
  if (item?.control_id) {
    return `/controls/${item.control_id}`;
  }
  return fallbackPath;
}

export default function GovernanceOverview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(emptySummary);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    getPolicySummary()
      .then((payload) => {
        if (cancelled) return;
        setSummary({ ...emptySummary, ...(payload || {}) });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || "Unable to load governance overview.");
        setSummary(emptySummary);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const cards = useMemo(() => {
    return widgets.map((widget) => {
      const value = summary?.[widget.key] || { count: 0, items: [] };
      const firstItem = Array.isArray(value.items) ? value.items[0] : null;
      const ctaPath =
        widget.key === "controls_without_published_policy"
          ? toControlPath(firstItem, widget.fallbackPath)
          : toPolicyPath(firstItem, widget.fallbackPath);

      return {
        ...widget,
        count: Number.isFinite(value.count) ? value.count : 0,
        ctaPath,
        ctaDisabled: !firstItem && !widget.fallbackPath,
      };
    });
  }, [summary]);

  return (
    <section style={sectionStyle}>
      <div style={{ display: "grid", gap: "6px", marginBottom: "14px" }}>
        <h2 style={{ margin: 0 }}>Governance Overview</h2>
        <p style={{ margin: 0, color: colors.textMuted, lineHeight: 1.5 }}>
          Monitor policy lifecycle risk and jump directly to impacted policy or control records.
        </p>
      </div>

      {error && (
        <PageState
          state="error"
          align="left"
          compact
          title="Governance overview unavailable"
          description={`${error} Refresh to try again.`}
          style={{ marginBottom: spacing.xl }}
        />
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "10px",
        }}
      >
        {cards.map((card) => (
          <article key={card.key} style={cardStyle}>
            <div>
              <div style={{ fontWeight: 700 }}>{card.title}</div>
              <div style={{ fontSize: typography.size.xs, color: colors.textMuted, marginTop: "3px" }}>{card.subtitle}</div>
            </div>

            <div style={{ fontSize: "30px", fontWeight: 700, color: colors.textPrimary, lineHeight: 1 }}>
              {loading ? "-" : card.count}
            </div>

            <Button
              variant="secondary"
              disabled={card.ctaDisabled}
              onClick={() => navigate(card.ctaPath)}
              style={{
                justifySelf: "start",
                padding: "6px 10px",
                borderRadius: radius.sm,
                border: `1px solid ${colors.border}`,
                background: colors.surface,
              }}
            >
              Open
            </Button>
          </article>
        ))}
      </div>
    </section>
  );
}
